import { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Avatar, Menu, MenuItem, ListItemIcon, Divider, useMediaQuery,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useProject } from '../context/ProjectContext';
import { getCurrentUser, isLoggedIn, logout } from '../utils/currentUser';
import { Wordmark } from './Brand';

function NavItem({ to, children, active }) {
  return (
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      {({ isActive }) => {
        const on = active ?? isActive;
        return (
          <Box sx={{
            px: 1.25, py: 0.5, borderRadius: 1, fontSize: '0.84rem', fontWeight: 500,
            color: on ? 'text.primary' : 'text.secondary',
            bgcolor: on ? 'action.hover' : 'transparent',
            transition: 'color 0.15s, background-color 0.15s',
            '&:hover': { color: 'text.primary' },
          }}>
            {children}
          </Box>
        );
      }}
    </NavLink>
  );
}

function TopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { projectName } = useProject();
  const narrow = useMediaQuery('(max-width:600px)');
  const [anchor, setAnchor] = useState(null);
  const user = getCurrentUser();
  const initials = ((user?.nickname || user?.email) ?? '?').slice(0, 2).toUpperCase();
  const inProject = pathname.startsWith('/app/') && !pathname.startsWith('/app/team');

  return (
    <Box component="header" sx={{
      position: 'sticky', top: 0, zIndex: (t) => t.zIndex.appBar,
      height: 48, px: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
      bgcolor: 'rgba(18,18,18,0.92)', backdropFilter: 'blur(8px)',
      borderBottom: '1px solid', borderColor: 'divider',
    }}>
      <Wordmark compact={narrow} />
      <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <NavItem to="/" active={pathname === '/'}>Home</NavItem>
        <NavItem to="/projects" active={pathname === '/projects' || inProject}>Projects</NavItem>
        {inProject && projectName && (
          <>
            <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
            <Box component={NavLink} to="/app/dashboard" sx={{
              fontSize: '0.84rem', color: 'text.primary', textDecoration: 'none',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 90, sm: 240 },
            }}>
              {projectName}
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Avatar
        src={user?.avatarUrl || undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          width: 28, height: 28, fontSize: '0.7rem', cursor: 'pointer',
          bgcolor: 'rgba(235,231,225,0.1)', color: 'text.primary',
          outline: anchor ? '2px solid' : 'none', outlineColor: 'divider',
        }}
      >
        {!user?.avatarUrl && initials}
      </Avatar>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography fontSize="0.85rem" fontWeight={600} noWrap>{user?.nickname || user?.email}</Typography>
          {user?.nickname && <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>}
        </Box>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); navigate('/app/team'); }} sx={{ fontSize: '0.85rem' }}>
          <ListItemIcon><GroupsOutlinedIcon fontSize="small" /></ListItemIcon>Team & profile
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); logout(); navigate('/'); }} sx={{ fontSize: '0.85rem' }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Log out
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Every page except the auth screens. The top bar is for signed-in users only.
export default function Layout() {
  useLocation(); // re-render on navigation so login/logout toggles the bar
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      {isLoggedIn() && <TopBar />}
      <Outlet />
    </Box>
  );
}

// Width-limited container for the workspace pages (projects, dashboards, team).
export function WorkspacePage() {
  return (
    <Box sx={{ width: '100%', maxWidth: 1680, mx: 'auto', px: { xs: 2, md: 3, lg: 4 }, py: { xs: 2.5, md: 3 } }}>
      <Outlet />
    </Box>
  );
}
